// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useRef } from 'react';
import { consoleService } from '../../services/consoleService';
import './Status.scss';

// Health status enum
enum NodeHealth {
  OK = 0,
  ERROR = 1,
  CONNECTING = 2,
  DISCONNECTING = 3
}

interface StatusNode {
  name: string;
  health: NodeHealth;
  session_count: number;
  presence_count: number;
  match_count: number;
  goroutine_count: number;
  avg_latency_ms: number;
  avg_rate_sec: number;
  avg_input_kbs: number;
  avg_output_kbs: number;
}

interface StatusList {
  nodes: StatusNode[];
  timestamp: string;
}

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="me-2" />
);

const GreenTickIcon = () => (
  <img src="/static/svg/green-tick.svg" alt="" width="15" height="15" className="me-2" />
);

const BlueSpinnerIcon = () => (
  <img src="/static/blue-spinner.svg" alt="" width="15" height="15" className="me-2" />
);

const RedSpinnerIcon = () => (
  <img src="/static/red-spinner.svg" alt="" width="15" height="15" className="me-2" />
);

const Status: React.FC = () => {
  const [error, setError] = useState('');
  const [showDelta, setShowDelta] = useState(false);
  const [statusData, setStatusData] = useState<StatusList | null>(null);
  const [rangeMinutes, setRangeMinutes] = useState(10);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const ranges = {
    1: 'last 1 minute',
    10: 'last 10 minutes',
    30: 'last 30 minutes',
    60: 'last 1 hour',
    1440: 'last 24 hours',
  };

  useEffect(() => {
    loadStatus();
    startRefreshTimer();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [rangeMinutes]);

  const loadStatus = async (isRefresh = false) => {
    try {
      setError('');
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await consoleService.getStatus('');
      setStatusData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load status');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const startRefreshTimer = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    const period = Math.floor((rangeMinutes * 60) / 60); // Refresh every period seconds
    refreshIntervalRef.current = setInterval(() => {
      loadStatus(true);
    }, period * 1000);
  };

  const handleRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRange = parseInt(event.target.value);
    setRangeMinutes(newRange);
  };

  const getHealthIcon = (health: NodeHealth) => {
    switch (health) {
      case NodeHealth.OK:
        return <GreenTickIcon />;
      case NodeHealth.ERROR:
        return <RedTriangleIcon />;
      case NodeHealth.CONNECTING:
        return <BlueSpinnerIcon />;
      case NodeHealth.DISCONNECTING:
        return <RedSpinnerIcon />;
      default:
        return <GreenTickIcon />;
    }
  };

  const getHealthTooltip = (health: NodeHealth) => {
    switch (health) {
      case NodeHealth.OK:
        return 'OK';
      case NodeHealth.ERROR:
        return 'Error';
      case NodeHealth.CONNECTING:
        return 'Connecting';
      case NodeHealth.DISCONNECTING:
        return 'Disconnecting';
      default:
        return 'Unknown';
    }
  };

  const getTotalSessionCount = (): number => {
    if (!statusData) return 0;
    return statusData.nodes.reduce((acc, v) => acc + v.session_count, 0);
  };

  const getMaxSessionCount = (): number => {
    if (!statusData) return 0;
    return Math.max(...statusData.nodes.map(e => e.session_count));
  };

  const getMaxPresenceCount = (): number => {
    if (!statusData) return 0;
    return Math.max(...statusData.nodes.map(e => e.presence_count));
  };

  const getMaxMatchCount = (): number => {
    if (!statusData) return 0;
    return Math.max(...statusData.nodes.map(e => e.match_count));
  };

  const getTotalMatchCount = (): number => {
    if (!statusData) return 0;
    return statusData.nodes.reduce((acc, v) => acc + v.match_count, 0);
  };

  const getMaxGoroutineCount = (): number => {
    if (!statusData) return 0;
    return Math.max(...statusData.nodes.map(e => e.goroutine_count));
  };

  const getTotalGoroutineCount = (): number => {
    if (!statusData) return 0;
    return statusData.nodes.reduce((acc, v) => acc + v.goroutine_count, 0);
  };

  if (loading) {
    return (
      <div className="status-container">
        <h2 className="pb-4">Status</h2>
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading status...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="status-container">
      <h2 className="pb-4">Status</h2>

      {error && (
        <div className="alert alert-danger mb-3">
          <RedTriangleIcon />
          <h6 className="d-inline fw-bold mb-0">An error occurred: {error}</h6>
        </div>
      )}

      <table 
        className="status-table table table-sm table-striped mb-4"
        onMouseEnter={() => setShowDelta(true)}
        onMouseLeave={() => setShowDelta(false)}
      >
        <thead className="table-light">
          <tr>
            <th scope="col">Node name</th>
            <th scope="col">Sessions</th>
            <th scope="col">Presences</th>
            <th scope="col">Authoritative Matches</th>
            <th scope="col">Goroutines</th>
          </tr>
        </thead>
        <tbody>
          {statusData && statusData.nodes.map((nodeData) => (
            <tr key={nodeData.name} className="clickable">
              <td>
                <div className="d-flex align-items-center">
                  <span title={getHealthTooltip(nodeData.health)}>
                    {getHealthIcon(nodeData.health)}
                  </span>
                  <span>{nodeData.name}</span>
                </div>
              </td>
              <td>
                {nodeData.session_count}
                {showDelta && (
                  <span className="text-muted small ms-1">
                    ({getMaxSessionCount() - nodeData.session_count} delta)
                  </span>
                )}
              </td>
              <td>
                {nodeData.presence_count}
                {showDelta && (
                  <span className="text-muted small ms-1">
                    ({getMaxPresenceCount() - nodeData.presence_count} delta)
                  </span>
                )}
              </td>
              <td>
                {nodeData.match_count}
                {showDelta && (
                  <span className="text-muted small ms-1">
                    ({getMaxMatchCount() - nodeData.match_count} delta)
                  </span>
                )}
              </td>
              <td>
                {nodeData.goroutine_count}
                {showDelta && (
                  <span className="text-muted small ms-1">
                    ({getMaxGoroutineCount() - nodeData.goroutine_count} delta)
                  </span>
                )}
              </td>
            </tr>
          ))}
          {statusData && (
            <tr className="table-light total-row">
              <td className="border-0"></td>
              <th className="fw-bold">{getTotalSessionCount()}</th>
              <th className="fw-bold">{getMaxPresenceCount()}</th>
              <th className="fw-bold">{getTotalMatchCount()}</th>
              <th className="fw-bold">{getTotalGoroutineCount()}</th>
            </tr>
          )}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="status-summary">
          {statusData && (
            <div className="d-flex gap-4">
              <div className="summary-item">
                <span className="summary-label">Total Sessions:</span>
                <span className="summary-value text-primary">{getTotalSessionCount()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Matches:</span>
                <span className="summary-value text-success">{getTotalMatchCount()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Nodes:</span>
                <span className="summary-value text-info">{statusData.nodes.length}</span>
              </div>
            </div>
          )}
        </div>
        <div className="status-controls d-flex align-items-center gap-3">
          <div className="d-flex align-items-center">
            <label className="form-label me-2 mb-0">View:</label>
            <select 
              className="form-select form-select-sm d-inline-block w-auto"
              value={rangeMinutes}
              onChange={handleRangeChange}
              disabled={loading || refreshing}
            >
              {Object.entries(ranges).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => loadStatus(true)}
            disabled={loading || refreshing}
            title="Refresh status data"
          >
            {refreshing ? (
              <>
                <div className="spinner-border spinner-border-sm me-1" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </button>
        </div>
      </div>

      {/* Placeholder for charts - would need a charting library like Chart.js or Recharts */}
      <div className="row mt-4">
        <div className="col-6">
          <div className="graph-title">
            <h5 className="d-inline">Processing Latency</h5>
            <small> (ms)</small>
          </div>
          <div className="graph">
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <span>Chart placeholder - Processing Latency</span>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="graph-title">
            <h5 className="d-inline">Rate</h5>
            <small> (rpc/s)</small>
          </div>
          <div className="graph">
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <span>Chart placeholder - Rate</span>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-6">
          <div className="graph-title">
            <h5 className="d-inline">Input</h5>
            <small> (kb/s)</small>
          </div>
          <div className="graph">
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <span>Chart placeholder - Input</span>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="graph-title">
            <h5 className="d-inline">Output</h5>
            <small> (kb/s)</small>
          </div>
          <div className="graph">
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <span>Chart placeholder - Output</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Status;
